import type { InputHTMLAttributes, ReactNode } from "react";
import { useId } from "react";
import { cn } from "@/lib/cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  /** Renders the field in the violation color with the message below. */
  error?: string | null;
}

export function Input({
  label,
  hint,
  error,
  className,
  id,
  ...rest
}: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label
          htmlFor={inputId}
          className="text-xs font-medium text-ink-muted"
        >
          {label}
        </label>
      ) : null}
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        className={cn(
          "h-8 w-full rounded-md border bg-surface px-2.5 text-sm text-ink",
          "transition-colors placeholder:text-ink-faint",
          "disabled:bg-surface-sunken disabled:text-ink-faint",
          error ? "border-violation" : "border-border-strong",
          className,
        )}
        {...rest}
      />
      {error ? (
        <p className="text-2xs text-violation">{error}</p>
      ) : hint ? (
        <p className="text-2xs text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}
